export const UTILITY_CONSUMPTION_UTILITY = {
	WATER: "water",
	GAS: "gas",
	ELECTRICITY: "electricity",
} as const;

export type UtilityConsumptionUtility =
	(typeof UTILITY_CONSUMPTION_UTILITY)[keyof typeof UTILITY_CONSUMPTION_UTILITY];

export const UTILITY_CONSUMPTION_VIEW = {
	DASHBOARD: "dashboard",
	REPORT: "report",
} as const;

export type UtilityConsumptionView =
	(typeof UTILITY_CONSUMPTION_VIEW)[keyof typeof UTILITY_CONSUMPTION_VIEW];

export const UTILITY_CONSUMPTION_MODE = {
	DAY: "day",
	WEEK: "week",
	MONTH: "month",
	DATE: "date",
} as const;

export type UtilityConsumptionMode =
	(typeof UTILITY_CONSUMPTION_MODE)[keyof typeof UTILITY_CONSUMPTION_MODE];
export const UTILITY_CONSUMPTION_CHART_METRIC = {
	GALLONS: "gallons",
	GALLONS_PER_TON: "gallonsPerTon",
} as const;

export type UtilityConsumptionChartMetric =
	(typeof UTILITY_CONSUMPTION_CHART_METRIC)[keyof typeof UTILITY_CONSUMPTION_CHART_METRIC];

export const UTILITY_CONSUMPTION_REPORT_MODE = {
	DAY: "day",
	WEEK: "week",
	MONTH: "month",
	YEAR: "year",
	PERIOD: "period",
	SECTION: "section",
} as const;

export type UtilityConsumptionReportMode =
	(typeof UTILITY_CONSUMPTION_REPORT_MODE)[keyof typeof UTILITY_CONSUMPTION_REPORT_MODE];

export type UtilityConsumptionReportCalendarMode = Exclude<
	Exclude<
		UtilityConsumptionReportMode,
		typeof UTILITY_CONSUMPTION_REPORT_MODE.PERIOD
	>,
	typeof UTILITY_CONSUMPTION_REPORT_MODE.SECTION
>;

export const UTILITY_CONSUMPTION_REPORT_ROW_KIND = {
	CALENDAR: "calendar",
	SECTION: "section",
} as const;

export const UTILITY_CONSUMPTION_DIRECTION = {
	PREVIOUS: "previous",
	NEXT: "next",
} as const;

export type UtilityConsumptionDirection =
	(typeof UTILITY_CONSUMPTION_DIRECTION)[keyof typeof UTILITY_CONSUMPTION_DIRECTION];

export interface UtilityConsumptionPeriod {
	startUtc: Date;
	endUtc: Date;
}

export interface UtilityConsumptionMeter {
	ExternalMeterId: string;
	MeterSerial: string | null;
	AssetId: number;
	MillName: string;
	WaterAccountId: number | null;
	Color: string;
	AccountNumber: string | null;
	AccountName: string | null;
	SharePercent: number;
}
export const getMeterLabel = (
	meter: Pick<UtilityConsumptionMeter, "ExternalMeterId" | "MeterSerial">,
): string => meter.ExternalMeterId;

export const createMeterNameResolver = (
	meters: Pick<UtilityConsumptionMeter, "ExternalMeterId" | "MeterSerial">[],
): ((meterId: string) => string) => {
	const nameById = new Map(
		meters.map((meter) => [meter.ExternalMeterId, getMeterLabel(meter)]),
	);
	return (meterId: string) => nameById.get(meterId) ?? meterId;
};

export const hasMultipleAccounts = (
	meters: UtilityConsumptionMeter[],
): boolean => new Set(meters.map((meter) => meter.WaterAccountId)).size > 1;

export interface UtilityConsumptionSummary {
	IsAvailable: boolean;
	DataThroughUtc: Date | null;
	Rolling24HourGallons: number | null;
	Last7Gallons: number | null;
	Previous7Gallons: number | null;
	SevenDayChangePercent: number | null;
	ThirtyDayAverageGallons: number | null;
	CurrentMonthLabel: string;
	PreviousMonthLabel: string;
	PreviousYearLabel: string;
	MonthToDateGallons: number | null;
	LastMonthTotalGallons: number | null;
	LastMonthBilledGallons: number | null;
	LastMonthBilledAmount: number | null;
	YearToDateGallons: number | null;
	PreviousYearGallons: number | null;
	AverageMonthGallons: number | null;
	AverageMonthCount: number;
	CurrentRollingSection: string | null;
	PreviousRollingSection: string | null;
	PreviousRollingStartUtc: Date | null;
	PreviousRollingEndUtc: Date | null;
	CurrentRollingGallonsPerTon: number | null;
	PreviousRollingGallonsPerTon: number | null;
	CurrentRollingCostPerTon: number | null;
	PreviousRollingCostPerTon: number | null;
	CostRatePerGallon: number | null;
	IsCostRateApproximate: boolean | null;
	CostRateBillStart: Date | null;
	CostRateBillEnd: Date | null;
	IsPreviousCostRateApproximate: boolean | null;
	MainTotalGallons: number | null;
	PeriodCostRatePerGallon: number | null;
	IsPeriodCostRateApproximate: boolean | null;
	PeriodCostRateBillStart: Date | null;
	PeriodCostRateBillEnd: Date | null;
	MainTotalCost: number | null;
	MainTotalTons: number | null;
	MainGallonsPerTon: number | null;
	IsMainPartial: boolean;
	GallonsPerTon: number | null;
	GallonsPerBillet: number | null;
}

export interface UtilityConsumptionMillRolling {
	MillAssetId: number;
	MillName: string;
	CurrentRollingSection: string | null;
	CurrentRollingStartUtc: Date | null;
	CurrentRollingGallonsPerTon: number | null;
	CurrentRollingCostPerTon: number | null;
	CostRatePerGallon: number | null;
	IsCostRateApproximate: boolean | null;
	CostRateBillStart: Date | null;
	CostRateBillEnd: Date | null;
}

export interface UtilityConsumptionMiniBucket {
	CurrentBucketStartUtc: Date | null;
	CurrentGallons: number | null;
	PreviousBucketStartUtc: Date;
	PreviousGallons: number | null;
}

export interface UtilityConsumptionMainBucket {
	BucketStartUtc: Date;
	ExternalMeterId: string;
	Gallons: number | null;
	GallonsPerTon: number | null;
}
export interface UtilityConsumptionAccountSplit {
	WaterAccountId: number;
	AccountNumber: string;
	TargetId: number;
	TargetCode: string;
	TargetName: string;
	SharePercent: number;
	IsFromBill: boolean;
}

export const UTILITY_CONSUMPTION_WINDOW = {
	LAST_7: "Last7",
	PREVIOUS_7: "Previous7",
	THIRTY_DAY: "ThirtyDay",
	MONTH_TO_DATE: "MonthToDate",
	LAST_MONTH: "LastMonth",
	YEAR_TO_DATE: "YearToDate",
	PREVIOUS_YEAR: "PreviousYear",
	COMPLETE_MONTHS: "CompleteMonths",
	CURRENT_ROLLING: "CurrentRolling",
} as const;

export type UtilityConsumptionWindow =
	(typeof UTILITY_CONSUMPTION_WINDOW)[keyof typeof UTILITY_CONSUMPTION_WINDOW];

export interface UtilityConsumptionMeterStatus {
	ExternalMeterId: string;
	LatestReadTimeUtc: Date | null;
	PeriodGallons: number | null;
	PeriodReadings: number;
	PeriodExpectedReadings: number;
}

export interface UtilityConsumptionMeterGap {
	WindowCode: UtilityConsumptionWindow;
	ExternalMeterId: string;
	LatestReadTimeUtc: Date | null;
}

export interface UtilityConsumptionData {
	summary: UtilityConsumptionSummary;
	millRolling: UtilityConsumptionMillRolling[];
	miniBuckets: UtilityConsumptionMiniBucket[];
	mainBuckets: UtilityConsumptionMainBucket[];
	accountSplits: UtilityConsumptionAccountSplit[];
	meterGaps: UtilityConsumptionMeterGap[];
	meterStatuses: UtilityConsumptionMeterStatus[];
}

export interface UtilityConsumptionRequest {
	targetId: number | null;
	externalMeterIds: string[] | null;
	mode: UtilityConsumptionMode;
	startUtc: Date;
	endUtc: Date;
	waitForEveryMeter?: boolean;
}

export interface UseUtilityConsumptionInput {
	targetId: number | null;
	enabled: boolean;
	utility: UtilityConsumptionUtility;
	externalMeterIds: string[] | null;
	mode: UtilityConsumptionMode;
	period: UtilityConsumptionPeriod;
	waitForEveryMeter?: boolean;
}

export interface UtilityConsumptionReportRange {
	startDateKey: string;
	endDateKey: string;
}

/**
 * One row per period (or event) and meter, as the report SP returns it: the
 * repository folds them into the rows below. Dates stay as the wire strings
 * because the calendar rows are grouped by `PeriodStartUtc`.
 */
export interface UtilityConsumptionReportMeterRow {
	PeriodStartUtc: string;
	ExternalMeterId: string;
	MeterGallons: number | null;
	MeterReadings: number | null;
	MeterExpectedReadings: number | null;
	PeriodGallons: number | null;
	MeterTotalGallons: number | null;
	PeriodEndUtc: string | null;
	SectionId: number | null;
	SectionName: string | null;
	EventId: number | null;
	EventStartUtc: string | null;
	EventEndUtc: string | null;
	MillAssetId: number | null;
	MillName: string | null;
	GallonsPerTon: number | null;
	GallonsPerBillet: number | null;
	PeriodCost: number | null;
	CostPerTon: number | null;
	IsCostApproximate: boolean;
}

export interface UtilityConsumptionReportSummary {
	GrandTotalGallons: number | null;
	GrandGallonsPerTon: number | null;
	GrandGallonsPerBillet: number | null;
	GrandTotalCost: number | null;
	GrandCostPerTon: number | null;
	IsCostApproximate: boolean;
}

export interface UtilityConsumptionReportCalendarRow {
	Kind: typeof UTILITY_CONSUMPTION_REPORT_ROW_KIND.CALENDAR;
	PeriodStartUtc: Date;
	MeterGallons: Record<string, number | null>;
	MeterCoverage: Record<string, number | null>;
	PeriodGallons: number | null;
	GallonsPerTon: number | null;
	GallonsPerBillet: number | null;
	PeriodCost: number | null;
	CostPerTon: number | null;
	IsCostApproximate: boolean;
}

export interface UtilityConsumptionReportSectionRow {
	Kind: typeof UTILITY_CONSUMPTION_REPORT_ROW_KIND.SECTION;
	MillAssetId: number;
	MillName: string;
	EventId: number;
	SectionId: number;
	SectionName: string;
	EventStartUtc: Date;
	EventEndUtc: Date | null;
	PeriodStartUtc: Date;
	PeriodEndUtc: Date;
	MeterGallons: Record<string, number | null>;
	MeterCoverage: Record<string, number | null>;
	PeriodGallons: number | null;
	GallonsPerTon: number | null;
	GallonsPerBillet: number | null;
	PeriodCost: number | null;
	CostPerTon: number | null;
	IsCostApproximate: boolean;
}

interface UtilityConsumptionReportRowsByKind {
	calendar: UtilityConsumptionReportCalendarRow;
	section: UtilityConsumptionReportSectionRow;
}

export type UtilityConsumptionReportRow =
	UtilityConsumptionReportRowsByKind[keyof UtilityConsumptionReportRowsByKind];

export interface UtilityConsumptionReportData {
	MeterIds: string[];
	Rows: UtilityConsumptionReportRow[];
	MeterTotalGallons: Record<string, number | null>;
	GrandTotalGallons: number | null;
	GrandGallonsPerTon: number | null;
	GrandGallonsPerBillet: number | null;
	GrandTotalCost: number | null;
	GrandCostPerTon: number | null;
	IsCostApproximate: boolean;
}

export interface UtilityConsumptionReportRequest {
	targetId: number | null;
	externalMeterIds: string[] | null;
	mode: UtilityConsumptionReportMode;
	sectionId: number | null;
	startUtc: Date;
	endUtc: Date;
}
